import { supabase } from '../supabaseClient';

// =================================================================
// ==   核心用戶數據 API (無變動)                                 ==
// =================================================================

export const getFullUserProfile = async (userId) => {
    if (!userId) throw new Error('User ID is required.');
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        username,
        avatar,
        background,
        wallpaper_item_id,
        total_xp,
        current_coins,
        settings:user_settings(
          user_id, language, is_new, gemini_api_key, error_status, 
          birthday, country, native_language, learning_goal
        ),
        status:user_status(
          id, user_id, language, sug_lvl, cur_lvl, 
          cur_content, cur_err, cur_log, 
          cur_report,
          init_test_result
        )
      `)
      .eq('id', userId)
      .single();
    if (error) {
        if (error.code === 'PGRST116') {
            console.warn(`Full user profile not found for user ${userId}. This might happen during initial setup.`);
            return null;
        }
        console.error('Error fetching full user profile:', error);
        throw new Error(error.message);
    }
    return data;
};

export const getUserData = async (userId) => {
  if (!userId) throw new Error('User ID is required.');
  const [profilePromise, settingsPromise] = await Promise.all([
    supabase.from('profiles').select('id, username').eq('id', userId).single(),
    supabase.from('user_settings').select('language, is_new').eq('user_id', userId).single()
  ]);
  if (profilePromise.error) {
    console.error('Error fetching user profile:', profilePromise.error);
    throw new Error(profilePromise.error.message);
  }
  if (settingsPromise.error) {
    console.error('Error fetching user settings:', settingsPromise.error);
    throw new Error(settingsPromise.error.message);
  }
  return { ...profilePromise.data, settings: settingsPromise.data };
};

export const updateUserProfile = async (userId, updates) => {
  if (!userId || !updates) throw new Error('User ID and updates are required.');
  const { data, error } = await supabase.from('profiles').update(updates).eq('id', userId).select().single();
  if (error) {
    console.error('Error updating user profile:', error);
    throw new Error(error.message);
  }
  return data;
};

export const updateUserSettings = async (userId, updates) => {
    if (!userId || !updates) throw new Error('User ID and updates are required.');
    const { data, error } = await supabase.from('user_settings').update(updates).eq('user_id', userId).select().single();
    if (error) {
      console.error('Error updating user settings:', error);
      throw new Error(error.message);
  }
  return data;
};

export const getPracticeLanguages = async () => {
  const { data, error } = await supabase
    .from('languages')
    .select('language, label, phoneme_system')
    .order('label', { ascending: true });

  if (error) {
    console.error('Error fetching practice languages:', error);
    throw new Error(error.message);
  }

  return (data || []).map((item) => ({
    value: item.language,
    label: item.label,
    phonemeSystem: item.phoneme_system || null,
  }));
};

export const getShopItems = async () => {
  const { data, error } = await supabase
    .from('shop_items')
    .select('id, code, name, type, price, path, is_active')
    .eq('is_active', true)
    .order('type', { ascending: true })
    .order('price', { ascending: true })
    .order('id', { ascending: true });

  if (error) {
    console.error('Error fetching shop items:', error);
    throw new Error(error.message);
  }

  return data || [];
};

export const getShopItemById = async (itemId) => {
  if (!itemId) return null;

  const { data, error } = await supabase
    .from('shop_items')
    .select('id, code, name, type, price, path, is_active')
    .eq('id', itemId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching shop item by id:', error);
    throw new Error(error.message);
  }

  return data || null;
};

export const getUserInventoryItemIds = async () => {
  const { data, error } = await supabase
    .from('user_inventory')
    .select('item_id')
    .order('acquired_at', { ascending: false });

  if (error) {
    console.error('Error fetching user inventory:', error);
    throw new Error(error.message);
  }

  return (data || []).map((item) => Number(item.item_id)).filter((itemId) => Number.isFinite(itemId));
};

export const purchaseShopItem = async (itemId) => {
  if (!itemId) throw new Error('Item ID is required.');

  const { data, error } = await supabase.rpc('purchase_shop_item', {
    p_item_id: itemId,
  });

  if (error) {
    console.error('Error purchasing shop item:', error);
    throw new Error(error.message);
  }

  return Array.isArray(data) ? data[0] || null : data;
};

// =================================================================
// ==   Tutor API                                                 ==
// =================================================================

export const getTutorApplications = async (userId) => {
  if (!userId) throw new Error('User ID is required.');

  // Fetch both applications and active profiles
  const [applicationsPromise, profilesPromise] = await Promise.all([
    supabase
      .from('tutor_applications')
      .select('*')
      .eq('uid', userId),
    supabase
      .from('tutors_profile')
      .select('*')
      .eq('uid', userId)
  ]);

  if (applicationsPromise.error) throw applicationsPromise.error;
  if (profilesPromise.error) throw profilesPromise.error;

  const applications = applicationsPromise.data || [];
  const profiles = profilesPromise.data || [];

  // Build maps for easy lookup
  const appMap = new Map();
  applications.forEach(a => {
    appMap.set(a.language, a);
  });

  const profileMap = new Map();
  profiles.forEach(p => {
    profileMap.set(p.language, p);
  });

  // ✨ 以 tutor_applications 為主要資料來源
  // Status 永遠從 tutor_applications 取，profile 只提供附加資料
  const combined = [];

  applications.forEach(app => {
    const profile = profileMap.get(app.language);
    combined.push({
      // 基本資料從 application
      uid: app.uid,
      language: app.language,
      full_name: app.full_name,
      self_introduction: app.self_introduction,
      proof_images: app.proof_images,
      applied_at: app.applied_at,
      // ✨ Status 永遠從 tutor_applications 取
      status: app.status || 'pending',
      reason: app.reason,
      appeal_message: app.appeal_message,
      // 從 profile 補充附加資料（如果存在）
      type: profile ? 'profile' : 'application',
      display_name: profile?.display_name,
      avatar: profile?.avatar,
      background: profile?.background,
      bio: profile?.bio,
      headline: profile?.headline,
      years_of_experience: profile?.years_of_experience,
      work_history: profile?.work_history
    });
  });

  return combined;
};

export const uploadTutorProof = async (file, language) => {
  if (!file || !language) throw new Error('File and Language are required.');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('language', language);

  const { data, error } = await supabase.functions.invoke('handle-tutor-applications', {
    body: formData,
  });

  if (error) {
    let errorPayload = null;
    if (error.context?.json) {
        try {
            errorPayload = await error.context.json();
        } catch {
            errorPayload = null;
        }
    }
    const errorMessage = errorPayload?.error || error.message;
    throw new Error(errorMessage || 'Image upload failed.');
  }

  return data.path;
};

export const submitTutorApplication = async (applicationData) => {
  const { uid, language, full_name, self_introduction, proof_images } = applicationData;
  if (!uid || !language || !full_name || !self_introduction) {
    throw new Error('Missing required fields.');
  }

  const { data, error } = await supabase
    .from('tutor_applications')
    .upsert([{
      uid,
      language,
      full_name,
      self_introduction,
      proof_images, // JSONB array of strings (paths)
      status: 'pending',
      reason: null,
      applied_at: new Date().toISOString()
    }], { onConflict: 'uid,language' })
    .select()
    .single();

    if (error) {
      console.error('Error getting tutor student profile:', error);
      throw error;
    }
    return data;
  };

/**
 * ✨ [新增] 導師為學生建立課程
 * @param {string} tutorId - 導師 ID (即當前登入者)
 * @param {string} studentId - 學生 ID
 * @param {object} courseData - 課程資料 { courseName, description, sentences, language }
 */
export const createTutorCourse = async (tutorId, studentId, courseData) => {
  if (!tutorId || !studentId || !courseData) {
    throw new Error('Missing required parameters for creating course.');
  }

  const { courseName, description, sentences, language } = courseData;

  // 1. 構建 course_contents JSONB
  const courseContents = {
    content: sentences, // string array
    description: description || '',
    length_of_content: sentences.length,
    internal_meta: {
      source: 'tutor_manual',
      generated_by: tutorId,
      created_at: new Date().toISOString()
    }
  };

  // 2. 插入資料庫
  const { data, error } = await supabase
    .from('courses')
    .insert([{
      uid: studentId,
      created_by: tutorId, // ✨ 關鍵：標記建立者
      course_name: courseName,
      course_contents: courseContents,
      language: language || 'en_us',
      generated_at: new Date().toISOString()
    }])
    .select()
    .single();

  if (error) {
    console.error('Error creating tutor course:', error);
    throw new Error(error.message);
  }

  return data;
};


export const approveTutorApplication = async ({ uid, language }) => {
  if (!uid || !language) throw new Error('UID and Language are required.');

  const { error } = await supabase.rpc('approve_tutor_application', {
    _application_uid: uid,
    _language: language
  });

  if (error) {
    console.error('Error approving application:', error);
    throw new Error(error.message);
  }
};

export const rejectTutorApplication = async ({ uid, language, reason }) => {
  if (!uid || !language || !reason) throw new Error('UID, Language, and Reason are required.');

  const { error } = await supabase.rpc('reject_tutor_application', {
    _application_uid: uid,
    _language: language,
    _reason: reason
  });

  if (error) {
    console.error('Error rejecting application:', error);
    throw new Error(error.message);
  }
};

export const getPublicTutors = async () => {
  const { data, error } = await supabase
    .from('tutors_profile')
    .select('*')
    .eq('status', 'active')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching public tutors:', error);
    throw new Error(error.message);
  }
  return data || [];
};

export const getPublicTutorProfile = async (uid, language) => {
  if (!uid || !language) throw new Error('UID and Language are required.');
  
  const { data, error } = await supabase
    .from('tutors_profile')
    .select('*')
    .eq('uid', uid)
    .eq('language', language)
    .eq('status', 'active')
    .single();

  if (error) {
    console.error('Error fetching public tutor profile:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 獲取當前使用者的所有導師身分
 */
export const getMyTutorProfiles = async (userId) => {
  if (!userId) throw new Error('User ID is required.');
  
  const { data, error } = await supabase
    .from('tutors_profile')
    .select('*')
    .eq('uid', userId);

  if (error) {
    console.error('Error fetching my tutor profiles:', error);
    throw new Error(error.message);
  }
  return data || [];
};


// =================================================================
// ==   初始測試 API (無變動)                                     ==
// =================================================================

export const postInitialTestResult = async (sessionData) => {
    if (!sessionData) throw new Error('Session data is required.');
    const { data, error } = await supabase.from('practice_sessions').insert([sessionData]).select().single();
    if (error) {
        console.error('Error posting initial test result:', error);
        throw new Error(error.message);
    }
    return data;
};

export const awardUserXp = async (userId, sourceType, correctRate = null) => {
  if (!userId) throw new Error('User ID is required.');
  if (!sourceType) throw new Error('Source type is required.');

  const { data, error } = await supabase.rpc('award_user_xp', {
    p_user_id: userId,
    p_source_type: sourceType,
    p_correct_rate: correctRate,
  });

  if (error) {
    console.error('Error awarding user xp:', error);
    throw new Error(error.message);
  }

  return data;
};

export const getActiveTutorProfilesByUserId = async (uid, language = '') => {
  if (!uid) throw new Error('UID is required.');

  let query = supabase
    .from('tutors_profile')
    .select('*')
    .eq('uid', uid)
    .eq('status', 'active');

  if (language && language !== 'all') {
    query = query.eq('language', language);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching active tutor profiles by user ID:', error);
    throw new Error(error.message);
  }

  return data || [];
};

export const markTestAsCompleted = async (userId, language, suggestedLevel = 'Primary-School') => {
    if (!userId || !language) throw new Error('User ID and language are required.');
    const { data, error } = await supabase.from('user_status').update({ sug_lvl: suggestedLevel }).eq('user_id', userId).eq('language', language).select().single();
    if (error) {
        console.error('Error marking test as completed:', error);
        throw new Error(error.message);
    }
    return data;
};

// =================================================================
// ==   狀態管理 API (無變動)                                     ==
// =================================================================

export const getInitialTestProgress = async (userId, language) => {
  if (!userId || !language) throw new Error('User ID and language are required.');
  
  // 【【【核心修正】】】
  // 在 select() 語句中加入了 'init_contents' 欄位。
  // 現在，這個 API 請求會將我們最需要的測驗內容資料也一併取回。
  const { data, error } = await supabase
    .from('user_status')
    .select('cur_lvl, cur_content, cur_log, cur_err, sug_lvl, init_contents') // <--- 修改點
    .eq('user_id', userId)
    .eq('language', language)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      console.warn(`No initial test progress found for user ${userId} in language ${language}. Returning default.`);
      // 返回的預設物件中也加入 init_contents: null，以保持資料結構一致性。
      return { cur_lvl: 'initial_test_0', cur_content: null, cur_log: null, cur_err: null, sug_lvl: null, init_contents: null }; // <--- 修改點
    }
    console.error('Error fetching initial test progress:', error);
    throw new Error(error.message);
  }
  
  return data;
};

export const updateInitialTestProgress = async (userId, language, updates) => {
  if (!userId || !language || !updates) throw new Error('User ID, language, and updates are required.');
  const { data, error } = await supabase.from('user_status').update(updates).eq('user_id', userId).eq('language', language).select().single();
  if (error) {
    console.error('Error updating initial test progress:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 為指定語言創建一個初始的 user_status 記錄。
 * 使用 .upsert() 來安全地處理：如果記錄已存在，則不做任何事；如果不存在，則插入。
 * @param {string} userId - 使用者 ID。
 * @returns {Promise<object>} - 創建或已存在的記錄。
 */
export const createInitialUserStatus = async (userId, language) => {
  if (!userId || !language) {
    throw new Error('User ID and language are required to create user status.');
  }

  const { data, error } = await supabase
    .from('user_status')
    .upsert(
      { 
        user_id: userId, 
        language: language,
        // ✨ 預設值：直接進入初始測試的第一個單字狀態
        cur_lvl: 'initial_test_1' 
      },
      { onConflict: 'user_id, language' } // ✨ 關鍵：如果 user_id 和 language 的組合已存在，則忽略插入
    )
    .select()
    .single();

  if (error) {
    console.error('Error creating initial user status:', error);
    throw new Error(error.message);
  }

  return data;
};

// =================================================================
// ==   練習記錄 & 進度摘要 API (已修改)                          ==
// =================================================================

/**
 * [無變動] 獲取指定用戶的所有練習記錄。
 * @param {string} userId - 用戶 ID。
 * @returns {Promise<Array>} - 練習記錄數組。
 */
export const getPracticeRecords = async (userId, language) => {
    if (!userId || !language) throw new Error('User ID and language are required.');
    const { data, error } = await supabase
      .from('practice_sessions')
      .select('*, full_report')
      .eq('user_id', userId)
      .eq('language', language)
      .order('created_at', { ascending: false });
    if (error) {
        console.error('Error fetching practice records:', error);
        throw new Error(error.message);
    }
    return data;
};

/**
 * ✨ [已修改] 透過 RPC 獲取指定用戶和語言的音標錯誤摘要。
 * @param {string} userId - 用戶 ID。
 * @returns {Promise<Array>} - 包含最多前 5 條音標錯誤記錄的陣列。
 */
export const getPhonemeSummary = async (userId, language) => {
  if (!userId || !language) throw new Error('User ID and language are required.');

  // ✨ 【【【核心修正 1】】】 ✨
  // 使用我們在後端定義的、新的、正確的函數名稱
  const { data, error } = await supabase.rpc('get_top_error_phonemes', {
    p_user_id: userId,
    p_language: language,
  });

  if (error) {
    console.error('Error fetching phoneme summary via RPC:', error);
    throw new Error(error.message);
  }
  
  if (!data) return [];

  // 統一輸出：前端圖表使用 err_amount 這個欄位名。
  // RPC 可能回傳 attempt_amount（或其他別名），這裡做兼容與數值化。
  return data.map((item) => {
    const rawAmount = item?.err_amount ?? item?.attempt_amount ?? item?.occurrence_count ?? 0;
    return { ...item, err_amount: Number(rawAmount) };
  });
};

/**
 * ✨ [新增] 呼叫 RPC 函數來更新使用者的整體進度摘要。
 * @param {string} userId - 使用者 ID。
 * @param {Array<object>} practiceErrors - 包含錯誤詳情的陣列，例如 [{ phoneme: '/a/', type: 'substitution' }]。
 * @returns {Promise<void>}
 */
export const updateUserProgressSummary = async (userId, language, stats) => { // 變數名也建議修改
  if (!userId || !language || !stats || stats.length === 0) {
    console.warn('updateUserProgressSummary skipped due to missing parameters or empty stats.');
    return;
  }

  const { error } = await supabase.rpc('update_user_progress_summary', {
    p_user_id: userId,
    p_language: language,
    p_stats: stats, // ✨ 參數名修正為 p_stats
  });

  if (error) {
    console.error('Error calling updateUserProgressSummary RPC:', error);
    throw new Error(error.message);
  }
};


// =================================================================
// ==   日常練習 API (已修改)                                     ==
// =================================================================

/**
 * ✨ [已修改] 透過 RPC 獲取用戶最弱的音素（最高錯誤率）。
 * @param {string} userId - 用戶 ID。
 * @returns {Promise<string>} - 最弱的音標字串，如果沒有則返回預設值 'i'。
 */
export const getWeakestPhoneme = async (userId, language) => {
  if (!userId || !language) return 'i';

  const { data, error } = await supabase.rpc('get_weakest_phoneme', {
    p_user_id: userId,
    p_language: language,
  });

  if (error) {
    console.error('Error fetching weakest phoneme:', error);
    return 'i'; // 出錯時返回預設值
  }
  return data || 'i';
};

/**
 * [無變動] 調用 Edge Function 來為用戶生成一個新的練習單字。
 * @param {object} params - 包含 phoneme, difficulty_level, language 的對象。
 * @returns {Promise<object>} - 從 LLM 服務返回的包含 practice_word 的對象。
 */
export const generatePracticeWord = async ({ phoneme, difficulty_level, language }) => {
  if (!phoneme || !difficulty_level || !language) {
    throw new Error('Phoneme, difficulty level, and language are required.');
  }
  const formData = new FormData();
  formData.append('phoneme', phoneme);
  formData.append('difficulty_level', difficulty_level);
  formData.append('language', language);
  const { data, error } = await supabase.functions.invoke('generate-practice-word', { body: formData });
  if (error) {
    console.error('Error generating practice word:', error);
    const errorMessage = error.context?.json?.()?.error || error.message;
    throw new Error(`Failed to generate a new word: ${errorMessage}`);
  }
  return data;
};

// =================================================================
// ==   Friends (Edge Function)                                    ==
// =================================================================

export const searchFriendById = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required.');
  }
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id, username, avatar, background')
    .eq('id', userId)
    .maybeSingle();

  if (profileError) {
    throw new Error(profileError.message || 'Failed to search user.');
  }
  if (!profile) return null;

  let country = '';
  const { data: settings, error: settingsError } = await supabase
    .from('user_public_info')
    .select('country')
    .eq('user_id', userId)
    .maybeSingle();

  if (!settingsError) {
    country = settings?.country || '';
  }

  return { ...profile, country };
};

export const sendFriendRequest = async (addresseeId) => {
  if (!addresseeId) {
    throw new Error('Addressee ID is required.');
  }
  const { data, error } = await supabase.functions.invoke('handle-friends', {
    body: { action: 'request', addresseeId },
  });
  if (error) {
    let errorPayload = null;
    if (error.context?.json) {
      try {
        errorPayload = await error.context.json();
      } catch {
        errorPayload = null;
      }
    }
    const errorMessage = errorPayload?.error || error.message;
    const err = new Error(errorMessage || 'Failed to send friend request.');
    if (errorPayload?.code) {
      err.code = errorPayload.code;
    }
    throw err;
  }
  return data;
};

export const getFriendRequestsFrontend = async (userId) => {
  if (!userId) return { pending: [], received: [] };

  const [{ data: pendingRaw, error: pendingError }, { data: receivedRaw, error: receivedError }] = await Promise.all([
    supabase
      .from('friend_requests')
      .select('id, requester_id, addressee_id, created_at')
      .eq('requester_id', userId)
      .order('created_at', { ascending: false }),
    supabase
      .from('friend_requests')
      .select('id, requester_id, addressee_id, created_at')
      .eq('addressee_id', userId)
      .order('created_at', { ascending: false }),
  ]);

  if (pendingError) throw pendingError;
  if (receivedError) throw receivedError;

  const pendingIds = (pendingRaw || []).map((item) => item.addressee_id);
  const receivedIds = (receivedRaw || []).map((item) => item.requester_id);
  const allIds = Array.from(new Set([...pendingIds, ...receivedIds]));

  if (allIds.length === 0) {
    return { pending: [], received: [] };
  }

  const [{ data: profiles, error: profileError }, { data: settings, error: settingsError }] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, username, avatar, background')
      .in('id', allIds),
    supabase
      .from('user_public_info')
      .select('user_id, country')
      .in('user_id', allIds),
  ]);

  if (profileError) throw profileError;
  const safeSettings = settingsError ? [] : (settings || []);

  const profileMap = new Map((profiles || []).map((item) => [item.id, item]));
  const settingsMap = new Map(safeSettings.map((item) => [item.user_id, item]));

  const buildUser = (id) => {
    const profile = profileMap.get(id) || {};
    const setting = settingsMap.get(id) || {};
    return {
      id,
      username: profile.username || '',
      avatar: profile.avatar || '',
      background: profile.background || '',
      country: setting.country || '',
    };
  };

  return {
    pending: (pendingRaw || []).map((request) => ({
      id: request.id,
      created_at: request.created_at,
      user: buildUser(request.addressee_id),
    })),
    received: (receivedRaw || []).map((request) => ({
      id: request.id,
      created_at: request.created_at,
      user: buildUser(request.requester_id),
    })),
  };
};

export const cancelFriendRequest = async (requestId) => {
  if (!requestId) {
    throw new Error('Request ID is required.');
  }
  const { error } = await supabase
    .from('friend_requests')
    .delete()
    .eq('id', requestId);

  if (error) {
    throw new Error(error.message || 'Failed to cancel friend request.');
  }
};

export const acceptFriendRequest = async (requestId) => {
  if (!requestId) {
    throw new Error('Request ID is required.');
  }
  const { data, error } = await supabase.functions.invoke('handle-friends', {
    body: { action: 'accept', requestId },
  });
  if (error) {
    let errorPayload = null;
    if (error.context?.json) {
      try {
        errorPayload = await error.context.json();
      } catch {
        errorPayload = null;
      }
    }
    const errorMessage = errorPayload?.error || error.message;
    const err = new Error(errorMessage || 'Failed to accept friend request.');
    if (errorPayload?.code) {
      err.code = errorPayload.code;
    }
    throw err;
  }
  return data;
};

export const declineFriendRequest = async (requestId) => {
  if (!requestId) {
    throw new Error('Request ID is required.');
  }
  const { data, error } = await supabase.functions.invoke('handle-friends', {
    body: { action: 'decline', requestId },
  });
  if (error) {
    let errorPayload = null;
    if (error.context?.json) {
      try {
        errorPayload = await error.context.json();
      } catch {
        errorPayload = null;
      }
    }
    const errorMessage = errorPayload?.error || error.message;
    const err = new Error(errorMessage || 'Failed to decline friend request.');
    if (errorPayload?.code) {
      err.code = errorPayload.code;
    }
    throw err;
  }
  return data;
};

export const getFriendships = async (userId) => {
  if (!userId) return [];

  // 查詢所有包含當前用戶的好友關係
  const { data: friendships, error: friendshipsError } = await supabase
    .from('friendships')
    .select('id, friend_one, friend_two, created_at')
    .or(`friend_one.eq.${userId},friend_two.eq.${userId}`)
    .order('created_at', { ascending: false });

  if (friendshipsError) throw friendshipsError;
  if (!friendships || friendships.length === 0) return [];

  // 取得好友的 ID（排除自己）
  const friendIds = friendships.map((f) =>
    f.friend_one === userId ? f.friend_two : f.friend_one
  );
  const uniqueFriendIds = Array.from(new Set(friendIds));

  // 批量查詢好友的 profiles 和 country
  const [{ data: profiles, error: profileError }, { data: settings, error: settingsError }] = await Promise.all([
    supabase
      .from('profiles')
      .select('id, username, avatar, background, total_xp')
      .in('id', uniqueFriendIds),
    supabase
      .from('user_public_info')
      .select('user_id, country')
      .in('user_id', uniqueFriendIds),
  ]);

  if (profileError) throw profileError;
  const safeSettings = settingsError ? [] : (settings || []);

  const profileMap = new Map((profiles || []).map((p) => [p.id, p]));
  const settingsMap = new Map(safeSettings.map((s) => [s.user_id, s]));

  // 組合結果
  return friendships.map((f) => {
    const friendId = f.friend_one === userId ? f.friend_two : f.friend_one;
    const profile = profileMap.get(friendId) || {};
    const setting = settingsMap.get(friendId) || {};
    return {
      friendshipId: f.id,
      id: friendId,
      username: profile.username || '',
      avatar: profile.avatar || '',
      background: profile.background || '',
      total_xp: Number(profile.total_xp) || 0,
      country: setting.country || '',
      created_at: f.created_at,
    };
  });
};

export const getGlobalLeaderboard = async () => {
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, username, avatar, total_xp')
    .order('total_xp', { ascending: false })
    .order('username', { ascending: true });

  if (profileError) {
    console.error('Error fetching global leaderboard:', profileError);
    throw new Error(profileError.message);
  }

  const profileList = profiles || [];
  if (profileList.length === 0) return [];

  const userIds = profileList.map((profile) => profile.id);
  const { data: settings, error: settingsError } = await supabase
    .from('user_public_info')
    .select('user_id, country')
    .in('user_id', userIds);

  if (settingsError) {
    console.error('Error fetching leaderboard public info:', settingsError);
    throw new Error(settingsError.message);
  }

  const settingsMap = new Map((settings || []).map((item) => [item.user_id, item]));

  return profileList.map((profile) => ({
    id: profile.id,
    username: profile.username || '',
    avatar: profile.avatar || '',
    total_xp: Number(profile.total_xp) || 0,
    country: settingsMap.get(profile.id)?.country || '',
  }));
};

const parseFunctionInvokeError = async (error, fallbackMessage) => {
  let errorPayload = null;
  if (error?.context?.json) {
    try {
      errorPayload = await error.context.json();
    } catch {
      errorPayload = null;
    }
  }
  const errorMessage = errorPayload?.error || error?.message || fallbackMessage;
  const wrappedError = new Error(errorMessage);
  if (errorPayload?.code) {
    wrappedError.code = errorPayload.code;
  }
  return wrappedError;
};

export const getDirectMessageSessions = async (userId) => {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return [];
  const normalizedUserIdLower = normalizedUserId.toLowerCase();

  let participantSessionIds = [];
  const { data: myParticipants, error: myParticipantsError } = await supabase
    .from('chat_participants')
    .select('session_id')
    .eq('involver', normalizedUserId);

  if (myParticipantsError) {
    console.warn('getDirectMessageSessions: chat_participants query failed, fallback to direct_pairs.', myParticipantsError);
  } else {
    participantSessionIds = Array.from(new Set((myParticipants || []).map((row) => row.session_id)));
  }

  let directPairs = [];
  const { data: pairRows, error: pairRowsError } = await supabase
    .from('chat_direct_pairs')
    .select('session_id, user_low, user_high, chat_type, context_key')
    .or(`user_low.eq.${normalizedUserId},user_high.eq.${normalizedUserId}`);

  if (pairRowsError) {
    console.warn('getDirectMessageSessions: chat_direct_pairs query failed, fallback to participants only.', pairRowsError);
  } else {
    directPairs = (pairRows || []).filter((row) => {
      const chatType = String(row.chat_type || '').trim().toLowerCase();
      const contextKey = String(row.context_key ?? '').trim();
      return chatType === 'direct_message' && contextKey === '';
    });
  }

  const pairSessionIds = Array.from(new Set(directPairs.map((row) => row.session_id)));
  const sessionIds = Array.from(new Set([...participantSessionIds, ...pairSessionIds]));
  if (sessionIds.length === 0) return [];

  const { data: sessions, error: sessionsError } = await supabase
    .from('chat_sessions')
    .select('id, type, created_at, last_message_at')
    .in('id', sessionIds);

  if (sessionsError) throw new Error(sessionsError.message);

  const dmSessions = (sessions || []).filter(
    (session) => String(session.type || '').trim().toLowerCase() === 'direct_message',
  );
  if (dmSessions.length === 0) return [];

  const dmSessionIds = dmSessions.map((session) => session.id);

  const [{ data: participants, error: participantsError }, { data: latestMessages, error: latestMessagesError }] =
    await Promise.all([
      supabase
        .from('chat_participants')
        .select('session_id, involver')
        .in('session_id', dmSessionIds),
      supabase
        .from('private_chat')
        .select('session_id, message, sent_at')
        .in('session_id', dmSessionIds)
        .order('sent_at', { ascending: false }),
    ]);

  if (participantsError) {
    console.warn('getDirectMessageSessions: participants detail query failed.', participantsError);
  }
  if (latestMessagesError) {
    console.warn('getDirectMessageSessions: latest message query failed.', latestMessagesError);
  }

  const participantMap = new Map();
  (participants || []).forEach((row) => {
    if (!participantMap.has(row.session_id)) {
      participantMap.set(row.session_id, []);
    }
    participantMap.get(row.session_id).push(String(row.involver));
  });

  const pairMap = new Map((directPairs || []).map((pair) => [pair.session_id, pair]));

  const peerIds = Array.from(
    new Set(
      dmSessions
        .map((session) => {
          const pair = pairMap.get(session.id);
          if (pair) {
            const low = String(pair.user_low || '');
            const high = String(pair.user_high || '');
            if (low.toLowerCase() === normalizedUserIdLower) return high;
            if (high.toLowerCase() === normalizedUserIdLower) return low;
          }
          const involvers = participantMap.get(session.id) || [];
          return involvers.find((id) => id.toLowerCase() !== normalizedUserIdLower) || null;
        })
        .filter(Boolean),
    ),
  );

  let profileMap = new Map();
  if (peerIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, avatar')
      .in('id', peerIds);

    if (profilesError) {
      console.warn('getDirectMessageSessions: profile query failed, fallback to raw id.', profilesError);
    } else {
      profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]));
    }
  }

  const latestMessageMap = new Map();
  (latestMessages || []).forEach((row) => {
    if (!latestMessageMap.has(row.session_id)) {
      latestMessageMap.set(row.session_id, row);
    }
  });

  const normalized = dmSessions.map((session) => {
    let peerId = '';
    const pair = pairMap.get(session.id);
    if (pair) {
      const low = String(pair.user_low || '');
      const high = String(pair.user_high || '');
      peerId = low.toLowerCase() === normalizedUserIdLower ? high : low;
    }
    if (!peerId) {
      const involvers = participantMap.get(session.id) || [];
      peerId = involvers.find((id) => id.toLowerCase() !== normalizedUserIdLower) || '';
    }

    const peerProfile = profileMap.get(peerId);
    const latestMessage = latestMessageMap.get(session.id);
    const updatedAtRaw = session.last_message_at || latestMessage?.sent_at || session.created_at;
    const updatedAt = updatedAtRaw ? new Date(updatedAtRaw).getTime() : Date.now();

    return {
      id: session.id,
      type: session.type,
      chatKind: 'dm',
      targetUserId: peerId,
      name: peerProfile?.username || peerId || 'Unknown user',
      peerAvatar: peerProfile?.avatar || '',
      subtitle: 'DM',
      preview: latestMessage?.message || 'No messages yet',
      updatedAt,
      isDraft: false,
    };
  });

  return normalized.sort((a, b) => b.updatedAt - a.updatedAt);
};

export const getChatMessages = async (sessionId, currentUserId) => {
  if (!sessionId) return [];

  const { data, error } = await supabase
    .from('private_chat')
    .select('message_seq, session_id, sender, message, sent_at')
    .eq('session_id', sessionId)
    .order('sent_at', { ascending: true });

  if (error) throw new Error(error.message);

  return (data || []).map((row) => ({
    id: row.message_seq,
    legacyId: null,
    messageSeq: row.message_seq ?? null,
    sessionId: row.session_id,
    sender: row.sender,
    text: row.message,
    at: row.sent_at ? new Date(row.sent_at).getTime() : Date.now(),
    role: row.sender === currentUserId ? 'me' : 'other',
    pending: false,
    failed: false,
  }));
};

export const createChatSession = async (targetUserId, type = 'direct_message', contextKey = '') => {
  if (!targetUserId) {
    throw new Error('targetUserId is required.');
  }

  const { data, error } = await supabase.functions.invoke('handle-create-chat-sessions', {
    body: {
      target_user_id: targetUserId,
      type,
      context_key: contextKey,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to create chat session.');
  }
  return data;
};

export const createTutorStudentChatSession = async (tutorStudentId, targetUserId = '') => {
  const normalizedTutorStudentId = String(tutorStudentId || '').trim();
  if (!normalizedTutorStudentId) {
    throw new Error('tutorStudentId is required.');
  }

  const normalizedTargetUserId = String(targetUserId || '').trim();
  const body = {
    type: 'tutor_student_chat',
    tutor_student_id: normalizedTutorStudentId,
  };

  if (normalizedTargetUserId) {
    body.target_user_id = normalizedTargetUserId;
  }

  const { data, error } = await supabase.functions.invoke('handle-create-chat-sessions', {
    body,
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to create tutor-student chat session.');
  }
  return data;
};

export const getTutorStudentChatSessions = async (userId) => {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return { myTutors: [], myStudents: [] };

  const { data: rows, error } = await supabase
    .from('tutor_student_chat_sessions')
    .select(`
      session_id,
      language,
      created_at,
      languages:language(label),
      tutor_students!inner(
        id,
        tutor_id,
        student_id,
        status,
        language
      ),
      chat_sessions!inner(
        id,
        type,
        created_at,
        last_message_at
      )
    `);

  if (error) throw new Error(error.message);

  const normalizedRows = (rows || []).map((row) => {
    const chatSessionRaw = row.chat_sessions;
    const chatSession = Array.isArray(chatSessionRaw) ? chatSessionRaw[0] : chatSessionRaw;
    const tutorStudentRaw = row.tutor_students;
    const tutorStudent = Array.isArray(tutorStudentRaw) ? tutorStudentRaw[0] : tutorStudentRaw;
    const languageMetaRaw = row.languages;
    const languageMeta = Array.isArray(languageMetaRaw) ? languageMetaRaw[0] : languageMetaRaw;
    return {
      sessionId: row.session_id,
      language: String(row.language || tutorStudent?.language || '').trim(),
      languageLabel: String(languageMeta?.label || '').trim(),
      createdAt: row.created_at || chatSession?.created_at || null,
      chatSession,
      tutorStudent,
    };
  }).filter((row) => {
    const type = String(row.chatSession?.type || '').trim().toLowerCase();
    const status = String(row.tutorStudent?.status || '').trim().toLowerCase();
    return type === 'tutor_student_chat' && status === 'accepted';
  });

  if (normalizedRows.length === 0) {
    return { myTutors: [], myStudents: [] };
  }

  const sessionIds = normalizedRows.map((row) => row.sessionId);
  const peerIds = Array.from(new Set(normalizedRows.map((row) => {
    const tutorId = String(row.tutorStudent?.tutor_id || '').trim();
    const studentId = String(row.tutorStudent?.student_id || '').trim();
    return tutorId === normalizedUserId ? studentId : tutorId;
  }).filter(Boolean)));
  const tutorPeerKeys = Array.from(new Set(
    normalizedRows
      .map((row) => {
        const tutorId = String(row.tutorStudent?.tutor_id || '').trim();
        if (!tutorId || tutorId === normalizedUserId) return '';
        const language = String(row.language || row.tutorStudent?.language || '').trim();
        return language ? `${tutorId}::${language}` : '';
      })
      .filter(Boolean),
  ));
  const tutorPeerIds = Array.from(new Set(
    tutorPeerKeys
      .map((item) => item.split('::')[0])
      .filter(Boolean),
  ));

  const [
    { data: latestMessages, error: latestMessagesError },
    { data: profiles, error: profilesError },
    { data: tutorProfiles, error: tutorProfilesError },
  ] = await Promise.all([
    supabase
      .from('tutor_student_chat')
      .select('session_id, sender, role, message, sent_at')
      .in('session_id', sessionIds)
      .order('sent_at', { ascending: false }),
    supabase
      .from('profiles')
      .select('id, username, avatar')
      .in('id', peerIds),
    tutorPeerIds.length === 0
      ? Promise.resolve({ data: [], error: null })
      : supabase
          .from('tutors_profile')
          .select('uid, language, full_name, display_name, avatar')
          .in('uid', tutorPeerIds)
          .eq('status', 'active'),
  ]);

  if (latestMessagesError) throw new Error(latestMessagesError.message);
  if (profilesError) throw new Error(profilesError.message);
  if (tutorProfilesError) throw new Error(tutorProfilesError.message);

  const latestMessageMap = new Map();
  (latestMessages || []).forEach((row) => {
    if (!latestMessageMap.has(row.session_id)) {
      latestMessageMap.set(row.session_id, row);
    }
  });

  const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]));
  const tutorProfileMap = new Map(
    (tutorProfiles || []).map((profile) => [`${String(profile.uid || '').trim()}::${String(profile.language || '').trim()}`, profile]),
  );

  const normalizedSessions = normalizedRows.map((row) => {
    const tutorId = String(row.tutorStudent?.tutor_id || '').trim();
    const studentId = String(row.tutorStudent?.student_id || '').trim();
    const isTutorView = tutorId === normalizedUserId;
    const peerId = isTutorView ? studentId : tutorId;
    const tutorProfileKey = !isTutorView
      ? `${peerId}::${String(row.language || row.tutorStudent?.language || '').trim()}`
      : '';
    const latestMessage = latestMessageMap.get(row.sessionId);
    const updatedAtRaw = row.chatSession?.last_message_at || latestMessage?.sent_at || row.createdAt;
    const updatedAt = updatedAtRaw ? new Date(updatedAtRaw).getTime() : Date.now();
    const peerProfile = profileMap.get(peerId);
    const peerTutorProfile = tutorProfileMap.get(tutorProfileKey);

    return {
      id: row.sessionId,
      type: 'tutor_student_chat',
      chatKind: 'tutor_student',
      targetUserId: peerId,
      tutorStudentId: String(row.tutorStudent?.id || '').trim(),
      language: row.language,
      languageLabel: row.languageLabel || (row.language ? String(row.language).replace('_', '-').toUpperCase() : 'General'),
      languageTag: row.language ? String(row.language).replace('_', '-').toUpperCase() : '',
      name: peerTutorProfile?.display_name || peerTutorProfile?.full_name || peerProfile?.username || peerId || 'Unknown user',
      peerAvatar: peerTutorProfile?.avatar || peerProfile?.avatar || '',
      subtitle: isTutorView ? 'Student' : 'Tutor',
      preview: latestMessage?.message || 'No messages yet',
      updatedAt,
      isDraft: false,
      participantRole: isTutorView ? 'tutor' : 'student',
      sectionKey: isTutorView ? 'my_students' : 'my_tutors',
    };
  }).sort((a, b) => b.updatedAt - a.updatedAt);

  return {
    myTutors: normalizedSessions.filter((session) => session.sectionKey === 'my_tutors'),
    myStudents: normalizedSessions.filter((session) => session.sectionKey === 'my_students'),
  };
};

export const getTutorStudentChatMessages = async (sessionId, currentUserId) => {
  if (!sessionId) return [];

  const { data, error } = await supabase
    .from('tutor_student_chat')
    .select('message_seq, session_id, sender, role, message, sent_at')
    .eq('session_id', sessionId)
    .order('sent_at', { ascending: true });

  if (error) throw new Error(error.message);

  return (data || []).map((row) => ({
    id: row.message_seq,
    legacyId: null,
    messageSeq: row.message_seq ?? null,
    sessionId: row.session_id,
    sender: row.sender,
    text: row.message,
    at: row.sent_at ? new Date(row.sent_at).getTime() : Date.now(),
    role: row.sender === currentUserId ? 'me' : 'other',
    senderRole: row.role || '',
    pending: false,
    failed: false,
  }));
};

export const getAiChatMessages = async (sessionId, currentUserId) => {
  if (!sessionId) return [];

  const { data, error } = await supabase
    .from('ai_chat')
    .select('message_seq, session_id, role, model_id, message, sent_at')
    .eq('session_id', sessionId)
    .order('sent_at', { ascending: true });

  if (error) throw new Error(error.message);

  return (data || []).map((row) => ({
    id: row.message_seq,
    legacyId: null,
    messageSeq: row.message_seq ?? null,
    sessionId: row.session_id,
    sender: row.role === 'user' ? currentUserId : row.role,
    text: row.message,
    at: row.sent_at ? new Date(row.sent_at).getTime() : Date.now(),
    role: row.role === 'user' ? 'me' : row.role === 'system' ? 'system' : 'other',
    kind: row.role === 'system' ? 'system_error' : 'chat',
    pending: false,
    failed: row.role === 'system',
    modelId: row.model_id || '',
    promptEligible: row.role !== 'system',
  }));
};

export const getAiChatSessions = async (userId) => {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return [];

  const { data: aiSessionRows, error: aiSessionError } = await supabase
    .from('ai_chat_sessions')
    .select(`
      session_id,
      user_id,
      session_name,
      language,
      chat_sessions!inner(id, type, created_at, last_message_at),
      languages:language(label)
    `)
    .eq('user_id', normalizedUserId);

  if (aiSessionError) throw new Error(aiSessionError.message);

  const normalizedSessions = (aiSessionRows || [])
    .map((row) => {
      const sessionRaw = row.chat_sessions;
      const session = Array.isArray(sessionRaw) ? sessionRaw[0] : sessionRaw;
      if (!session || String(session.type || '').trim().toLowerCase() !== 'ai_chat') return null;
      const languageMetaRaw = row.languages;
      const languageMeta = Array.isArray(languageMetaRaw) ? languageMetaRaw[0] : languageMetaRaw;
      return {
        sessionId: row.session_id,
        sessionName: String(row.session_name || '').trim() || 'New Chat',
        language: String(row.language || '').trim(),
        languageLabel: String(languageMeta?.label || '').trim(),
        createdAt: session.created_at,
        lastMessageAt: session.last_message_at,
      };
    })
    .filter(Boolean);

  if (normalizedSessions.length === 0) return [];

  const sessionIds = normalizedSessions.map((row) => row.sessionId);

  const { data: aiMessages, error: aiMessagesError } = await supabase
    .from('ai_chat')
    .select('session_id, role, model_id, message, sent_at')
    .in('session_id', sessionIds)
    .order('sent_at', { ascending: false });

  if (aiMessagesError) {
    throw new Error(aiMessagesError.message);
  }

  const latestMessageMap = new Map();
  const latestModelIdBySession = new Map();
  const modelIds = new Set();

  (aiMessages || []).forEach((row) => {
    if (!latestMessageMap.has(row.session_id)) {
      latestMessageMap.set(row.session_id, row);
    }
    if (!latestModelIdBySession.has(row.session_id) && row.model_id) {
      latestModelIdBySession.set(row.session_id, row.model_id);
      modelIds.add(row.model_id);
    }
  });

  let modelMap = new Map();
  if (modelIds.size > 0) {
    const { data: models, error: modelsError } = await supabase
      .from('ai_models')
      .select(`
        model_id,
        display_name,
        provider,
        family,
        ai_model_families!ai_models_provider_family_fkey(
          display_name,
          icon
        )
      `)
      .in('model_id', Array.from(modelIds));

    if (modelsError) {
      console.warn('getAiChatSessions: ai_models query failed, fallback to model id only.', modelsError);
    } else {
      modelMap = new Map((models || []).map((model) => [model.model_id, model]));
    }
  }

  return normalizedSessions
    .map((row) => {
      const latestMessage = latestMessageMap.get(row.sessionId);
      const currentModelId = latestModelIdBySession.get(row.sessionId) || '';
      const model = currentModelId ? modelMap.get(currentModelId) : null;
      const familyMetaRaw = model?.ai_model_families;
      const familyMeta = Array.isArray(familyMetaRaw) ? familyMetaRaw[0] : familyMetaRaw;
      const updatedAtRaw = row.lastMessageAt || latestMessage?.sent_at || row.createdAt;
      const updatedAt = updatedAtRaw ? new Date(updatedAtRaw).getTime() : Date.now();

      return {
        id: row.sessionId,
        type: 'ai_chat',
        chatKind: 'ai',
        targetUserId: '',
        name: row.sessionName,
        peerAvatar: familyMeta?.icon || '',
        subtitle: 'AI',
        preview: latestMessage?.message || 'No messages yet',
        updatedAt,
        isDraft: false,
        currentModelId,
        currentModelName: model?.display_name || currentModelId || 'No model selected',
        currentModelProvider: model?.provider || '',
        language: row.language || '',
        languageLabel: row.languageLabel || '',
      };
    })
    .sort((a, b) => b.updatedAt - a.updatedAt);
};

export const getAvailableAiModels = async () => {
  const { data, error } = await supabase
    .from('ai_models')
    .select(`
      model_id,
      display_name,
      provider,
      family,
      is_legacy,
      is_available,
      ai_model_families!ai_models_provider_family_fkey(
        display_name,
        icon
      )
    `)
    .eq('is_available', true)
    .eq('is_legacy', false)
    .order('provider', { ascending: true })
    .order('display_name', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return (data || []).map((model) => {
    const familyMetaRaw = model.ai_model_families;
    const familyMeta = Array.isArray(familyMetaRaw) ? familyMetaRaw[0] : familyMetaRaw;

    return {
      id: String(model.model_id || '').trim(),
      modelId: String(model.model_id || '').trim(),
      displayName: String(model.display_name || model.model_id || '').trim(),
      provider: String(model.provider || '').trim(),
      family: String(model.family || '').trim(),
      familyDisplayName: String(familyMeta?.display_name || '').trim(),
      icon: String(familyMeta?.icon || '').trim(),
    };
  }).filter((model) => model.modelId);
};

export const createAiChatSession = async (language = null) => {
  const normalizedLanguage = String(language || '').trim();
  const { data, error } = await supabase.functions.invoke('handle-create-ai-chat-session', {
    body: {
      language: normalizedLanguage || null,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to create ai chat session.');
  }
  return data;
};

export const deleteAiChatSession = async (sessionId) => {
  const normalizedSessionId = String(sessionId || '').trim();
  if (!normalizedSessionId) throw new Error('sessionId is required.');

  const { data, error } = await supabase.functions.invoke('handle-delete-ai-chat-session', {
    body: {
      session_id: normalizedSessionId,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to delete ai chat session.');
  }

  return data;
};

export const updateAiChatSessionName = async (sessionId, sessionName) => {
  const normalizedSessionId = String(sessionId || '').trim();
  const normalizedSessionName = String(sessionName || '').trim().replace(/\s+/g, ' ');

  if (!normalizedSessionId) throw new Error('sessionId is required.');
  if (!normalizedSessionName) throw new Error('sessionName is required.');

  const { data, error } = await supabase.functions.invoke('handle-update-ai-chat-session', {
    body: {
      session_id: normalizedSessionId,
      session_name: normalizedSessionName,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to update AI chat session name.');
  }

  return data;
};

export const deleteAiChatTurnMessages = async (sessionId, userMessageSeq = null, systemMessageSeq = null) => {
  const normalizedSessionId = String(sessionId || '').trim();
  const normalizedUserMessageSeq = Number.isFinite(userMessageSeq) && Number(userMessageSeq) > 0
    ? Number(userMessageSeq)
    : null;
  const normalizedSystemMessageSeq = Number.isFinite(systemMessageSeq) && Number(systemMessageSeq) > 0
    ? Number(systemMessageSeq)
    : null;

  if (!normalizedSessionId) throw new Error('sessionId is required.');

  const { data, error } = await supabase.functions.invoke('handle-delete-ai-chat-turn', {
    body: {
      session_id: normalizedSessionId,
      user_message_seq: normalizedUserMessageSeq,
      system_message_seq: normalizedSystemMessageSeq,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to delete failed AI chat turn.');
  }

  return data;
};

export const startAiChatSession = async (sessionId) => {
  if (!sessionId) throw new Error('sessionId is required.');

  const { data, error } = await supabase.functions.invoke('handle-ai-chat-session', {
    body: {
      session_id: sessionId,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to start ai chat session.');
  }

  return data;
};

export const createCourseDraftRequest = async ({ studentId, language, requestJson, draftId = null }) => {
  if (!studentId) throw new Error('studentId is required.');
  if (!requestJson || typeof requestJson !== 'object' || Array.isArray(requestJson)) {
    throw new Error('requestJson must be a JSON object.');
  }

  const { data, error } = await supabase.functions.invoke('handle-create-course-drafts', {
    body: {
      student_id: studentId,
      language: language || 'en_us',
      request_json: requestJson,
      ...(draftId ? { draft_id: draftId } : {}),
    },
  });

  if (error) {
    let errorPayload = null;
    if (error.context?.json) {
      try {
        errorPayload = await error.context.json();
      } catch {
        errorPayload = null;
      }
    }
    const message = errorPayload?.error || errorPayload?.message || error.message;
    throw new Error(message || 'Failed to create course draft request.');
  }

  return data;
};

export const moveDraftToCourse = async ({ draftId, courseName = '', description = '', sentences = [] }) => {
  if (!draftId) throw new Error('draftId is required.');

  const { data, error } = await supabase.functions.invoke('handle-draft-to-courses', {
    body: {
      draft_id: draftId,
      course_name: courseName,
      description,
      content: sentences,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to move draft to course.');
  }

  return data;
};

export const deleteTutorCourse = async (courseId) => {
  const normalizedCourseId = String(courseId || '').trim();
  if (!normalizedCourseId) throw new Error('courseId is required.');

  const { data, error } = await supabase.functions.invoke('handle-delete-courses', {
    body: {
      course_id: normalizedCourseId,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to delete course.');
  }

  return data;
};

export const clearCourseDraftGeneratedContent = async (draftId, requestJson = null) => {
  if (!draftId) throw new Error('draftId is required.');
  const updates = { generated_json: null };
  if (requestJson && typeof requestJson === 'object' && !Array.isArray(requestJson)) {
    updates.request_json = requestJson;
  }
  const { error } = await supabase
    .from('course_generation_drafts')
    .update(updates)
    .eq('id', draftId);

  if (error) {
    throw new Error(error.message || 'Failed to clear generated draft content.');
  }
  return true;
};

export const updateCourseDraftContent = async (draftId, sentences, courseName, description) => {
  if (!draftId) throw new Error('draftId is required.');
  if (!Array.isArray(sentences)) throw new Error('sentences must be an array.');

  // Fetch both request_json and generated_json to update them
  const { data: existing, error: fetchError } = await supabase
    .from('course_generation_drafts')
    .select('request_json, generated_json')
    .eq('id', draftId)
    .single();

  if (fetchError) {
    throw new Error(fetchError.message || 'Failed to fetch draft to update.');
  }

  const updatedGeneratedJson = {
    ...(existing?.generated_json || {}),
    content: sentences,
  };

  const updatedRequestJson = {
    ...(existing?.request_json || {}),
  };

  // Only update course_name and description if they are provided
  if (courseName !== undefined) {
    updatedRequestJson.course_name = courseName;
  }
  if (description !== undefined) {
    updatedRequestJson.description = description;
  }

  const { error: updateError } = await supabase
    .from('course_generation_drafts')
    .update({ 
      generated_json: updatedGeneratedJson,
      request_json: updatedRequestJson
    })
    .eq('id', draftId);

  if (updateError) {
    throw new Error(updateError.message || 'Failed to update draft content.');
  }
  return true;
};

export const getTutorStudentCourseDrafts = async (tutorId, studentId, language) => {
  if (!tutorId || !studentId) {
    throw new Error('tutorId and studentId are required.');
  }

  let query = supabase
    .from('course_generation_drafts')
    .select('id, tutor_id, student_id, language, request_json, generated_json, created_at, updated_at')
    .eq('tutor_id', tutorId)
    .eq('student_id', studentId)
    .order('updated_at', { ascending: false });

  if (language) {
    query = query.eq('language', language);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message || 'Failed to fetch course drafts.');
  }
  return data || [];
};

export const deleteCourseDraft = async (draftId) => {
  if (!draftId) throw new Error('draftId is required.');
  const { error } = await supabase.from('course_generation_drafts').delete().eq('id', draftId);
  if (error) throw new Error(error.message || 'Failed to delete course draft.');
  return true;
};

export const sendChatMessage = async (sessionId, message, clientTempId = null) => {
  if (!sessionId) throw new Error('sessionId is required.');
  if (!message?.trim()) throw new Error('message is required.');

  const { data, error } = await supabase.functions.invoke('handle-send-chat-message', {
    body: {
      session_id: sessionId,
      message,
      client_temp_id: clientTempId,
    },
  });

  if (error) {
    throw await parseFunctionInvokeError(error, 'Failed to send chat message.');
  }
  return data;
};

export const markChatSessionRead = async (sessionId, involver, readAt = null) => {
  const normalizedSessionId = String(sessionId || '').trim();
  const normalizedInvolver = String(involver || '').trim().toLowerCase();
  if (!normalizedSessionId) throw new Error('sessionId is required.');
  if (!normalizedInvolver) throw new Error('involver is required.');

  const nextReadAt = readAt || new Date().toISOString();
  const { data, error } = await supabase
    .from('chat_participants')
    .update({ last_read_at: nextReadAt })
    .eq('session_id', normalizedSessionId)
    .eq('involver', normalizedInvolver)
    .select('id, session_id, involver, last_read_at');

  if (error) {
    throw new Error(error.message);
  }
  if (!data || data.length === 0) {
    throw new Error('No participant row updated. Check RLS policy or involver value.');
  }
  return data[0];
};

export const getUnreadCountsBySessions = async (currentUserId, sessionIds = []) => {
  const normalizedUserId = String(currentUserId || '').trim().toLowerCase();
  const normalizedSessionIds = Array.from(
    new Set(
      (sessionIds || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    ),
  );

  if (!normalizedUserId || normalizedSessionIds.length === 0) return {};

  const unreadMap = {};
  normalizedSessionIds.forEach((sessionId) => {
    unreadMap[sessionId] = 0;
  });

  const { data, error } = await supabase
    .from('chat_unread_session_counts')
    .select('session_id, unread_count')
    .in('session_id', normalizedSessionIds)
    .eq('involver', normalizedUserId);

  if (error) throw new Error(error.message);

  (data || []).forEach((row) => {
    if (!row?.session_id) return;
    unreadMap[row.session_id] = Number(row.unread_count || 0);
  });

  return unreadMap;
};

export const getTutorStudentUnreadCountsBySessions = async (currentUserId, sessionIds = []) => {
  const normalizedUserId = String(currentUserId || '').trim().toLowerCase();
  const normalizedSessionIds = Array.from(
    new Set(
      (sessionIds || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    ),
  );

  if (!normalizedUserId || normalizedSessionIds.length === 0) return {};

  const unreadMap = {};
  normalizedSessionIds.forEach((sessionId) => {
    unreadMap[sessionId] = 0;
  });

  const { data, error } = await supabase
    .from('tutor_student_unread_session_counts')
    .select('session_id, unread_count')
    .in('session_id', normalizedSessionIds)
    .eq('involver', normalizedUserId);

  if (error) throw new Error(error.message);

  (data || []).forEach((row) => {
    if (!row?.session_id) return;
    unreadMap[row.session_id] = Number(row.unread_count || 0);
  });

  return unreadMap;
};

export const getTotalUnreadCount = async (currentUserId) => {
  const normalizedUserId = String(currentUserId || '').trim().toLowerCase();
  if (!normalizedUserId) return 0;

  const { data, error } = await supabase
    .from('chat_unread_totals')
    .select('total_unread_count')
    .eq('involver', normalizedUserId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return Number(data?.total_unread_count || 0);
};

export const getProfilesLastOnline = async (userIds = []) => {
  const normalized = Array.from(
    new Set(
      (userIds || [])
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    ),
  );

  if (normalized.length === 0) return {};

  const { data, error } = await supabase
    .from('profiles')
    .select('id, last_online_at')
    .in('id', normalized);

  if (error) throw new Error(error.message);

  const out = {};
  (data || []).forEach((row) => {
    out[row.id] = row.last_online_at || null;
  });
  return out;
};

export const removeFriend = async (friendshipId) => {
  if (!friendshipId) {
    throw new Error('Friendship ID is required.');
  }
  const { data, error } = await supabase.functions.invoke('handle-friends', {
    body: { action: 'remove_friend', friendshipId },
  });
  if (error) {
    let errorPayload = null;
    if (error.context?.json) {
      try {
        errorPayload = await error.context.json();
      } catch {
        errorPayload = null;
      }
    }
    const errorMessage = errorPayload?.error || error.message;
    const err = new Error(errorMessage || 'Failed to remove friend.');
    if (errorPayload?.code) {
      err.code = errorPayload.code;
    }
    throw err;
  }
  return data;
};

/**
 * [無變動] 獲取指定用戶和語言的練習狀態。
 * @param {string} userId - 用戶 ID。
 * @returns {Promise<object>} - 包含 cur_lvl, cur_content, 和 cur_log 的 user_status 對象。
 */
export const getPracticeStatus = async (userId, language) => {
  if (!userId || !language) return null;
  const { data, error } = await supabase.from('user_status').select('cur_lvl, cur_content, cur_log, cur_err, cur_report').eq('user_id', userId).eq('language', language).single();
  if (error) {
    if (error.code === 'PGRST116') {
      return { cur_lvl: 'Primary-School', cur_content: null, cur_log: null };
    }
    console.error('Error fetching practice status:', error);
    throw error;
  }
  return data;
};

/**
 * ✨ [新增] 觸發後端生成初始測驗回饋的 Edge Function。
 * @returns {Promise<object>} - Edge Function 的響應。
 */
export const triggerInitialFeedbackGeneration = async (language, finalSession = null) => {
  if (!language) {
    throw new Error('Language is required to trigger feedback generation.');
  }
  // Change function name to 'generate-courses' as per project structure
  const requestBody = { language: language };
  if (finalSession) {
    requestBody.final_session = finalSession;
  }
  const { data, error } = await supabase.functions.invoke('generate-courses', {
    body: requestBody,
  });

  if (error) {
    console.error('Error invoking generate-courses function:', error);
    throw error;
  }

  // Transform the raw response to match the component's expected format
  if (data && data.courses && Array.isArray(data.courses)) {
      data.courses = data.courses.map(c => ({
          title: c.name,
          description: c.description,
          difficulty_level: data.sug_lvl || 'Personalized',
          topics: [], // Default to empty if not present
          length: c.length
      }));
  }

  return data;
};

/**
 * ✨ [新增] 將初始測驗報告標記為已讀。
 * @param {string} userId - 使用者 ID。
 * @param {object} currentResult - 當前從資料庫讀取的 init_test_result 物件。
 * @returns {Promise<object>} - 更新後的 user_status 記錄。
 */
export const markInitialTestResultAsRead = async (userId, language, currentResult) => {
  if (!userId || !language || !currentResult) {
    throw new Error('User ID, language, and current result are required.');
  }
  
  const updatedResult = { ...currentResult, isRead: true };

  const { data, error } = await supabase
    .from('user_status')
    .update({ init_test_result: updatedResult })
    .match({ user_id: userId, language: language });

  if (error) {
    console.error('Error marking initial test result as read:', error);
    throw error;
  }
  return data;
};

/**
 * ✨ [新增] 呼叫後端服務為日常練習生成一個新的句子。
 * 這個函數會觸發一個包含數據庫讀取、AI 計算和數據庫寫入的完整流程。
 * @param {string} difficulty - 當前的難度等級。
 * @param {string} geminiApiKey - 使用者的 Gemini API Key。
 * @returns {Promise<object>} - Edge Function 的響應。
 */
export const getNewPracticeSentence = async ({ language, difficulty, geminiApiKey }) => {
  if (!language || !difficulty || !geminiApiKey) {
    throw new Error('Language, difficulty, and API key are required to generate a new sentence.');
  }

  // 注意：函數名 'generate-single-sentence' 必須與您部署的 Edge Function 名稱完全一致
  const { data, error } = await supabase.functions.invoke('generate-single-sentence', {
    body: {
      language: language,
      difficulty: difficulty,
      gemini_api_key: geminiApiKey,
    },
  });

    if (error) {

      console.error('Error invoking generate-single-sentence function:', error);

      throw error;

    }

    return data;

  };

  

  /**

   * ✨ [新增] 清除使用者的錯誤狀態 (將 error_status 設為 null)

   * @param {string} userId - 使用者 ID。


   * @returns {Promise<void>}

   */

  export const clearUserErrorStatus = async (userId) => {

    if (!userId) return;

    

    const { error } = await supabase

      .from('user_settings')

      .update({ error_status: null })

      .match({ user_id: userId });

  

    if (error) {

      console.error('Error clearing user error status:', error);

      throw error;

    }

  };

/**
 * ✨ [新增] 獲取使用者的生成課程列表。
 * @param {string} userId - 使用者 ID。
 * @returns {Promise<Array>} - 課程列表。
 */
export const getUserCourses = async (userId, language) => {
  if (!userId) throw new Error('User ID is required to fetch courses.');

  let query = supabase
    .from('courses')
    .select('*, course_status(cur_lvl, is_completed, completed_at)')
    .eq('uid', userId);

  if (language) {
    query = query.eq('language', language);
  }

  const { data, error } = await query.order('generated_at', { ascending: false });

  if (error) {
    console.error('Error fetching user courses:', error);
    throw new Error(error.message);
  }
  
  // Transform the data to match the component's expected format if necessary
  // The DB stores course content in 'course_contents' JSONB column
  // Expected format in component: { title: string, description: string, topics: string[], difficulty_level: string }
  return data.map(course => {
    const content = course.course_contents || {};
    const statusRecord = Array.isArray(course.course_status)
      ? course.course_status[0]
      : course.course_status || null;
    let currentIndex = 0;
    if (statusRecord?.cur_lvl) {
      const parts = statusRecord.cur_lvl.split('_');
      if (parts.length === 2 && parts[0] === 'index') {
        const parsed = parseInt(parts[1], 10);
        currentIndex = Number.isNaN(parsed) ? 0 : parsed;
      }
    }
    return {
      cid: course.cid,
      title: course.course_name,
      created_by: typeof course.created_by === 'string' ? course.created_by.trim() : course.created_by,
      description: content.description,
      // If your JSONB structure has other fields like topics or level, map them here.
      // Based on the user provided sample: 
      // { "course_name": "...", "description": "...", "length_of_content": "75 practices" }
      difficulty_level: content.difficulty_level || content.internal_meta?.level_hint || 'Personalized', 
      topics: content.topics || [], // If topics are not in JSON, defaults to empty array
      length: content.length_of_content,
      content_count: Array.isArray(content.content) ? content.content.length : null,
      current_index: currentIndex,
      completed_count: currentIndex, // ✨ Fix: Map currentIndex to completed_count for ManageStudentModal
      status: statusRecord ? { ...statusRecord } : null,
      language: course.language || content.internal_meta?.target_language,
      generated_at: course.generated_at
    };
  }).sort((a, b) => {
    // Sort by progress percentage (highest to lowest)
    const progressA = a.length > 0 ? ((a.completed_count || 0) / a.length) : 0;
    const progressB = b.length > 0 ? ((b.completed_count || 0) / b.length) : 0;
    return progressB - progressA;
  });
};

/**
 * ✨ [新增] 獲取指定課程的詳細資訊 (包含內容)。
 * @param {string} courseId - 課程 ID (cid)。
 * @returns {Promise<object>} - 課程物件。
 */
export const getCourse = async (courseId) => {
  if (!courseId) throw new Error('Course ID is required.');
  
  const { data, error } = await supabase
    .from('courses')
    .select('*')
    .eq('cid', courseId)
    .single();

  if (error) {
    console.error('Error fetching course:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 獲取指定課程的狀態。
 * @param {string} courseId - 課程 ID (cid)。
 * @returns {Promise<object>} - 課程狀態物件。
 */
export const getCourseStatus = async (courseId) => {
  if (!courseId) throw new Error('Course ID is required.');
  
  const { data, error } = await supabase
    .from('course_status')
    .select('*')
    .eq('cid', courseId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Status does not exist yet
    }
    console.error('Error fetching course status:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 為指定課程創建初始狀態。
 * @param {string} courseId - 課程 ID (cid)。
 * @returns {Promise<object>} - 創建的狀態物件。
 */
export const createCourseStatus = async (courseId) => {
  if (!courseId) throw new Error('Course ID is required.');

  // Fetch course content to get the first sentence
  const { data: course, error: courseError } = await supabase
    .from('courses')
    .select('course_contents')
    .eq('cid', courseId)
    .single();

  if (courseError) {
    console.error('Error fetching course for status creation:', courseError);
  }

  const firstContent = course?.course_contents?.content?.[0] || null;

  // Default initial status
  const initialStatus = {
    cid: courseId,
    cur_lvl: 'index_0', // Start at index 0
    cur_content: firstContent, // ✅ Set first sentence
    cur_log: null,
    cur_err: null,
    is_completed: false
  };

  const { data, error } = await supabase
    .from('course_status')
    .insert([initialStatus])
    .select()
    .single();

  if (error) {
    console.error('Error creating course status:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 更新課程狀態。
 * @param {string} courseId - 課程 ID (cid)。
 * @param {object} updates - 更新內容。
 * @returns {Promise<object>} - 更新後的狀態物件。
 */
export const updateCourseStatus = async (courseId, updates) => {
  if (!courseId || !updates) throw new Error('Course ID and updates are required.');

  const { data, error } = await supabase
    .from('course_status')
    .update(updates)
    .eq('cid', courseId)
    .select()
    .single();

  if (error) {
    console.error('Error updating course status:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 觸發課程內容生成 (Edge Function)。
 * @param {string} courseId - 課程 ID (cid)。
 * @param {boolean} force - 是否強制重新生成 (預設 false)。
 * @returns {Promise<object>} - Edge Function 回應。
 */
export const triggerCourseContentGeneration = async (courseId, force = false) => {
  if (!courseId) throw new Error('Course ID is required.');

  const { data, error } = await supabase.functions.invoke('generate-course-contents', {
    body: { course_id: courseId, force: force }
  });

  if (error) {
    console.error('Error triggering course content generation:', error);
    throw error;
  }
  return data;
};

/**
 * ✨ [新增] 檢查使用者的特殊角色 (Admin, Tutor)。
 * @param {string} userId - 使用者 ID。
 * @returns {Promise<object>} - { isAdmin: boolean, isTutor: boolean }
 */
export const getUserRoles = async (userId) => {
  if (!userId) return { isAdmin: false, isTutor: false };

  // 平行查詢兩個表，效率最高
  const [adminCheck, tutorCheck] = await Promise.all([
    supabase.from('admins').select('admin_id').eq('admin_id', userId).maybeSingle(),
    supabase.from('tutors_profile').select('uid').eq('uid', userId).eq('status', 'active').limit(1).maybeSingle()
  ]);

  return {
    isAdmin: !!adminCheck.data,
    isTutor: !!tutorCheck.data
  };
};

// =================================================================
// ==   Admin API                                                 ==
// =================================================================

/**
 * 獲取所有待審核的導師申請 (僅限管理員)
 */
export const getAdminPendingApplications = async () => {
  // 1. Fetch pending applications
  const { data: applications, error } = await supabase
    .from('tutor_applications')
    .select(`
      *,
      profile:profiles(avatar)
    `)
    .eq('status', 'pending')
    .order('applied_at', { ascending: false });

  if (error) {
    console.error('Error fetching pending applications:', error);
    throw new Error(error.message);
  }

  if (!applications || applications.length === 0) return [];

  // 2. Fetch history from tutors_profile
  // We check if these users have a record in tutors_profile (e.g. rejected status)
  const uids = applications.map(app => app.uid);
  const { data: profiles, error: profileError } = await supabase
    .from('tutors_profile')
    .select('uid, language, status, reason')
    .in('uid', uids);

  if (profileError) {
    console.error('Error fetching tutor profiles for history:', profileError);
    // Continue without history if this fails
  }

  // 3. Attach history to applications
  return applications.map(app => {
    // Find matching profile for the same language
    const history = profiles?.find(p => p.uid === app.uid && p.language === app.language);
    
    return {
      ...app,
      avatar: app.profile?.avatar,
      history_status: history?.status || null,
      history_reason: history?.reason || null
    };
  });
};

/**
 * 獲取所有導師資料 (僅限管理員) - 包含申請資料
 * ✨ 修正邏輯：優先使用 tutors_profile 的 reason 和 proof_images，解決歷史被覆蓋問題
 */
export const getAdminTutors = async () => {
  // 1. 先獲取所有 tutors_profile (包含 reason 和 proof_images)
  const { data: tutors, error: tutorError } = await supabase
    .from('tutors_profile')
    .select('*')
    .order('created_at', { ascending: false });

  if (tutorError) {
    console.error('Error fetching admin tutors:', tutorError);
    throw new Error(tutorError.message);
  }

  if (!tutors || tutors.length === 0) return [];

  // 2. 獲取對應的 pending applications (為了查看是否有重新申請)
  const { data: applications, error: appError } = await supabase
    .from('tutor_applications')
    .select('uid, language, self_introduction, proof_images, reason, appeal_message, status');

  if (appError) {
    console.error('Error fetching tutor applications:', appError);
    // 繼續執行
  }

  // 建立 Map 來快速查找
  const appMap = new Map();
  (applications || []).forEach(app => {
    appMap.set(`${app.uid}-${app.language}`, app);
  });

  // 3. 合併資料
  return tutors.map(tutor => {
    const app = appMap.get(`${tutor.uid}-${tutor.language}`);
    const isReapplying = app && app.status === 'pending' && (tutor.status === 'rejected' || tutor.status === 'paused');

    return {
      ...tutor,
      // 如果正在重新申請 (Pending)，顯示新申請的自介，否則顯示 Profile 內的
      self_introduction: isReapplying ? app.self_introduction : (tutor.bio || app?.self_introduction),
      
      // ✨ 關鍵修正：證明文件
      // 預設顯示 Profile 內的存檔 (tutor.proof_images)。
      // 如果正在重新申請，可以考慮顯示新的，但這裡我們主要依賴 Profile 的歷史紀錄。
      // 若 Profile 沒資料 (舊資料)，則回退到 App 的資料。
      proof_images: tutor.proof_images || app?.proof_images,
      
      // ✨ 關鍵修正：原因 (Reason)
      // 永遠優先顯示 Profile 中的 reason (歷史原因)，
      // 除非該欄位為空，才顯示 App 的 reason。
      // 這樣即使 App 被新申請覆蓋 (reason=null)，我們還能看到 Profile 裡的拒絕原因。
      reason: tutor.reason || app?.reason,
      
      appeal_message: app?.appeal_message,
      
      // 標記是否為重新申請中，供前端 UI 參考 (可選)
      isReapplying: !!isReapplying
    };
  });
};

/**
 * 暫停導師 (僅限管理員) - 使用 RPC 原子操作
 */
export const pauseTutor = async ({ uid, language, reason }) => {
  if (!uid || !language || !reason) throw new Error('UID, Language, and Reason are required.');

  const { error } = await supabase.rpc('pause_tutor', {
    _uid: uid,
    _language: language,
    _reason: reason
  });

  if (error) {
    console.error('Error pausing tutor:', error);
    throw new Error(error.message);
  }
};

/**
 * 恢復暫停的導師 (僅限管理員) - 使用 RPC 原子操作
 */
export const reactivateTutor = async ({ uid, language }) => {
  if (!uid || !language) throw new Error('UID and Language are required.');

  const { error } = await supabase.rpc('reactivate_tutor', {
    _uid: uid,
    _language: language
  });

  if (error) {
    console.error('Error reactivating tutor:', error);
    throw new Error(error.message);
  }
};

/**
 * 撤銷活躍/暫停的導師資格 (僅限管理員) - 使用 RPC 原子操作
 */
export const rejectActiveTutor = async ({ uid, language, reason }) => {
  if (!uid || !language || !reason) throw new Error('UID, Language, and Reason are required.');

  const { error } = await supabase.rpc('reject_active_tutor', {
    _uid: uid,
    _language: language,
    _reason: reason
  });

  if (error) {
    console.error('Error rejecting tutor:', error);
    throw new Error(error.message);
  }
};

/**
 * 用戶提交 Appeal 訊息
 */
export const submitAppeal = async ({ uid, language, message }) => {
  if (!uid || !language || !message) throw new Error('UID, Language, and Message are required.');

  // 先獲取現有的 appeal_message
  const { data: existing, error: fetchError } = await supabase
    .from('tutor_applications')
    .select('appeal_message')
    .eq('uid', uid)
    .eq('language', language)
    .single();

  if (fetchError) {
    console.error('Error fetching existing appeal:', fetchError);
    throw new Error(fetchError.message);
  }

  // 組合新訊息
  const existingMessages = existing?.appeal_message || [];
  const newMessage = {
    role: 'tutor',
    message: message,
    at: new Date().toISOString()
  };

  const { error: updateError } = await supabase
    .from('tutor_applications')
    .update({ 
      appeal_message: [...existingMessages, newMessage]
    })
    .eq('uid', uid)
    .eq('language', language);

  if (updateError) {
    console.error('Error submitting appeal:', updateError);
    throw new Error(updateError.message);
  }
};

/**
 * 管理員回覆 Appeal 訊息
 */
export const replyAppeal = async ({ uid, language, message }) => {
  if (!uid || !language || !message) throw new Error('UID, Language, and Message are required.');

  // 先獲取現有的 appeal_message
  const { data: existing, error: fetchError } = await supabase
    .from('tutor_applications')
    .select('appeal_message')
    .eq('uid', uid)
    .eq('language', language)
    .single();

  if (fetchError) {
    console.error('Error fetching existing appeal:', fetchError);
    throw new Error(fetchError.message);
  }

  // 組合新訊息
  const existingMessages = existing?.appeal_message || [];
  const newMessage = {
    role: 'admin',
    message: message,
    at: new Date().toISOString()
  };

  const { error: updateError } = await supabase
    .from('tutor_applications')
    .update({ 
      appeal_message: [...existingMessages, newMessage]
    })
    .eq('uid', uid)
    .eq('language', language);

  if (updateError) {
    console.error('Error replying to appeal:', updateError);
    throw new Error(updateError.message);
  }
};


export const getTutorStudents = async (tutorId, language) => {
  if (!tutorId) throw new Error('Tutor ID is required.');

  // 1. Get relationships (filter by language if provided)
  let query = supabase
    .from('tutor_students')
    .select('*')
    .eq('tutor_id', tutorId);
  
  if (language) {
    query = query.eq('language', language);
  }
  
  const { data: relations, error: relError } = await query.order('created_at', { ascending: false });

  if (relError) {
    console.error('Error fetching tutor students:', relError);
    throw new Error(relError.message);
  }
  
  if (!relations || relations.length === 0) return [];

  // 2. Get student profiles
  const studentIds = relations.map(r => r.student_id);
  
  // Use Promise.all to fetch profiles and their settings/status in parallel if needed
  // For now, let's just fetch profiles and basic public info
  const { data: profiles, error: profileError } = await supabase
    .from('profiles')
    .select('id, username, avatar, background')
    .in('id', studentIds);

  if (profileError) {
    console.error('Error fetching student profiles:', profileError);
    throw new Error(profileError.message);
  }

  // 3. Get student settings (now from user_public_info for public access)
  const { data: settings, error: settingsError } = await supabase
    .from('user_public_info')
    .select('user_id, country, birthday, learning_goal')
    .in('user_id', studentIds);

  if (settingsError) {
    console.error('Error fetching student public info:', settingsError);
    // Don't throw, just continue with partial data
  }

  // 4. Merge data
  const profileMap = new Map(profiles.map(p => [p.id, p]));
  const settingsMap = new Map((settings || []).map(s => [s.user_id, s]));

  return relations.map(relation => {
    const profile = profileMap.get(relation.student_id) || {};
    const setting = settingsMap.get(relation.student_id) || {};
    
    return {
      relationship_id: relation.id,
      student_id: relation.student_id,
      tutor_language: relation.language,
      status: relation.status,
      action_by: relation.action_by,
      created_at: relation.created_at,
      username: profile.username || 'Unknown User',
      avatar: profile.avatar,
      background: profile.background,
      country: setting.country,
      native_language: setting.native_language,
      learning_goal: setting.learning_goal,
      current_language: setting.language,
      birthday: setting.birthday // ✨ Add birthday
    };
  });
};

export const updateTutorStudentStatus = async (relationshipId, status) => {
  if (!relationshipId || !status) throw new Error('Relationship ID and Status are required.');
  
  const { data, error } = await supabase
    .from('tutor_students')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', relationshipId)
    .select()
    .single();

  if (error) {
    console.error('Error updating tutor-student status:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] Tutor 主動添加學生
 */
export const addTutorStudent = async (tutorId, studentId, language) => {
  if (!tutorId || !studentId || !language) {
    throw new Error('Tutor ID, Student ID, and Language are required.');
  }

  // Check if relationship already exists
  const { data: existing, error: checkError } = await supabase
    .from('tutor_students')
    .select('id, status')
    .eq('tutor_id', tutorId)
    .eq('student_id', studentId)
    .eq('language', language)
    .maybeSingle();

  if (checkError) {
    console.error('Error checking existing relationship:', checkError);
    throw new Error(checkError.message);
  }

  if (existing) {
    throw new Error(`This student is already ${existing.status === 'accepted' ? 'your student' : 'in ' + existing.status + ' status'}.`);
  }

  // Create new relationship
  const { data, error } = await supabase
    .from('tutor_students')
    .insert({
      tutor_id: tutorId,
      student_id: studentId,
      language: language,
      status: 'pending',
      action_by: 'tutor'
    })
    .select()
    .single();

  if (error) {
    console.error('Error adding tutor student:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [New] Delete a tutor-student relationship (Cancel Invite)
 */
export const deleteTutorStudent = async (relationshipId) => {
  if (!relationshipId) throw new Error('Relationship ID is required.');

  const { error } = await supabase
    .from('tutor_students')
    .delete()
    .eq('id', relationshipId);

  if (error) {
    console.error('Error deleting tutor-student relationship:', error);
    throw new Error(error.message);
  }
};

/**
 * ✨ [新增] 獲取特定語言的導師個人資料
 */
export const getTutorProfile = async (userId, language) => {
  if (!userId || !language) throw new Error('User ID and language are required.');

  const { data, error } = await supabase
    .from('tutors_profile')
    .select('*')
    .eq('uid', userId)
    .eq('language', language)
    .single();

  if (error) {
    console.error('Error fetching tutor profile:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * ✨ [新增] 更新特定語言的導師個人資料
 */

export const updateTutorProfile = async (userId, language, updates) => {
  if (!userId || !language || !updates) throw new Error('User ID, language, and updates are required.');

  const { data, error } = await supabase
    .from('tutors_profile')
    .update(updates)
    .eq('uid', userId)
    .eq('language', language)
    .select()
    .single();

  if (error) {
    console.error('Error updating tutor profile:', error);
    throw new Error(error.message);
  }
  return data;
};

export const getPublicSetting = async (settingName) => {
  if (!settingName) throw new Error('Setting name is required.');
  const { data, error } = await supabase
    .from('public_settings')
    .select('value')
    .eq('setting_name', settingName)
    .single();
  if (error) {
    console.error('Error fetching public setting:', error);
    throw new Error(error.message);
  }
  return data?.value || '';
};

// =================================================================
// ==   Student Tutor API (New for Tutor Hub)                     ==
// =================================================================

/**
 * Get available tutors for a specific language.
 */
export const getAvailableTutors = async (language) => {
  let query = supabase
    .from('tutors_profile')
    .select('*')
    .eq('status', 'active');

  if (language) {
    query = query.eq('language', language);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching available tutors:', error);
    throw new Error(error.message);
  }
  return data || [];
};

/**
 * Get all tutor connections for a student.
 */
export const getStudentTutorConnections = async (studentId) => {
  if (!studentId) throw new Error('Student ID is required.');

  // 1. Get relationships
  const { data: relations, error: relError } = await supabase
    .from('tutor_students')
    .select('*')
    .eq('student_id', studentId)
    .order('updated_at', { ascending: false });

  if (relError) {
    console.error('Error fetching student tutor connections:', relError);
    throw new Error(relError.message);
  }

  if (!relations || relations.length === 0) return [];

  // 2. Get tutor profiles
  const tutorIds = relations.map(r => r.tutor_id);
  
  // Fetch from tutors_profile to get professional info
  const { data: tutorProfiles, error: profileError } = await supabase
    .from('tutors_profile')
    .select('*')
    .in('uid', tutorIds);

  if (profileError) {
    console.error('Error fetching tutor profiles:', profileError);
    // Continue with partial data if needed, but error is bad
    throw new Error(profileError.message);
  }

  // 3. Merge data
  // We match tutor profiles by UID AND Language because a tutor might have multiple profiles (one per language)
  // But wait, the relationship has `language`. We should match that.
  
  return relations.map(relation => {
    const profile = tutorProfiles.find(p => p.uid === relation.tutor_id && p.language === relation.language) || {};
    
    return {
      relationship_id: relation.id,
      tutor_id: relation.tutor_id,
      student_id: relation.student_id,
      language: relation.language,
      status: relation.status,
      action_by: relation.action_by,
      created_at: relation.created_at,
      updated_at: relation.updated_at,
      // Profile Data
      full_name: profile.full_name || 'Unknown Tutor',
      display_name: profile.display_name,
      avatar: profile.avatar,
      headline: profile.headline,
      bio: profile.bio
    };
  });
};

/**
 * Student requests a tutor.
 */
export const requestTutor = async (tutorId, studentId, language) => {
  if (!tutorId || !studentId || !language) {
    throw new Error('Tutor ID, Student ID, and Language are required.');
  }

  // Check if relationship exists
  const { data: existing, error: checkError } = await supabase
    .from('tutor_students')
    .select('id, status')
    .eq('tutor_id', tutorId)
    .eq('student_id', studentId)
    .eq('language', language)
    .maybeSingle();

  if (checkError) throw new Error(checkError.message);

  if (existing) {
    // If rejected or cancelled (if we have a cancelled status), maybe allow re-request?
    // For now, simple check.
    throw new Error(`Request already exists with status: ${existing.status}`);
  }

  const { data, error } = await supabase
    .from('tutor_students')
    .insert({
      tutor_id: tutorId,
      student_id: studentId,
      language: language,
      status: 'pending',
      action_by: 'student'
    })
    .select()
    .single();

  if (error) {
    console.error('Error requesting tutor:', error);
    throw new Error(error.message);
  }
  return data;
};

/**
 * Update student-tutor connection status (Accept invite, Cancel request).
 */
export const updateStudentTutorStatus = async (relationshipId, status) => {
  if (!relationshipId || !status) throw new Error('Relationship ID and Status are required.');

  const { data, error } = await supabase
    .from('tutor_students')
    .update({ 
        status: status, 
        updated_at: new Date().toISOString() 
    })
    .eq('id', relationshipId)
    .select()
    .single();

  if (error) {
    console.error('Error updating connection status:', error);
    throw new Error(error.message);
  }
  return data;
};





  


